import React from "react";
import {
  Building2,
  HeartHandshake,
  ShieldCheck,
  CalendarCheck2,
  Wallet,
  Users,
  BadgeCheck,
  ArrowRight,
  Building2Icon,
} from "lucide-react";
import { motion } from "framer-motion";

const CorporateSupport = () => {
  const benefits = [
    {
      title: "Healthcare Coordination Support",
      description:
        "Dedicated coordination support for employees during healthcare needs and emergencies.",
      icon: <HeartHandshake size={26} className="text-royalBlue" strokeWidth={1.5} />,
    },
    {
      title: "Reduced Employee Stress",
      description:
        "Helps employees avoid the stress of searching for reliable healthcare providers independently.",
      icon: <ShieldCheck size={26} className="text-royalGreen" strokeWidth={1.5} />,
    },
    {
      title: "2500+ Verified Providers",
      description:
        "Access trusted hospitals, clinics, labs, therapists, and healthcare specialists across India.",
      icon: <Building2 size={26} className="text-royalBlue" strokeWidth={1.5} />,
    },
    {
      title: "Appointment Assistance",
      description:
        "Livmo helps coordinate appointments and healthcare access efficiently.",
      icon: <CalendarCheck2 size={26} className="text-royalGreen" strokeWidth={1.5} />,
    },
    {
      title: "Treatment Budget Guidance",
      description:
        "Support in understanding treatment costs and healthcare budgeting guidance.",
      icon: <Wallet size={26} className="text-royalBlue" strokeWidth={1.5} />,
    },
    {
      title: "Support Away From Family",
      description:
        "Additional coordination support for employees working far from their hometowns.",
      icon: <Users size={26} className="text-royalGreen" strokeWidth={1.5} />,
    },
  ];

  return (
    <motion.section
      id="CorporateBenefits"
      initial={{ opacity: 0, x: 40 }}
      whileInView={{ opacity: 1, x: 0 }}
      transition={{
        duration: 0.8,
        ease: "easeIn"
      }}
      viewport={{ once: true, amount: 0.3 }}
      className="bg-gradient-to-b from-[#F8FAFF] to-white py-[6vw] lg:py-[3vw] overflow-hidden relative"
    >
      {/* Background Effects - responsive */}
      <div className="absolute top-[-5%] right-[-5%] w-[200px] h-[200px] lg:w-[320px] lg:h-[320px] bg-royalBlue/5 rounded-full blur-3xl"></div>
      <div className="absolute bottom-[-10%] left-[-5%] w-[200px] h-[200px] lg:w-[320px] lg:h-[320px] bg-royalGreen/5 rounded-full blur-3xl"></div>

      <div className="max-w-[1450px] mx-auto px-[5%] relative z-10">
        
        {/* Heading */}
        <div className="text-center">
        {/* Pill Header - Copy this block, change the text only */}
<div className="flex justify-center mb-8 md:mb-10">
  <div className="inline-flex items-center gap-2 bg-gradient-to-r from-royalBlue/10 to-royalBlue/5 backdrop-blur-sm rounded-full px-5 py-2 sm:px-6 md:px-7 sm:py-2 md:py-3 border border-royalBlue/20 shadow-lg">
    <Building2Icon size={18} className="text-royalGreen" />
    <p className="font-poppins font-semibold text-royalBlue text-sm sm:text-base md:text-lg tracking-wide uppercase whitespace-nowrap">
      BENEFITS FOR SUBSCRIBERS
    </p>
  </div>
</div>

          <h2 className="mt-2.5 font-poppins font-[700] text-darkblue leading-[1.25] text-[7vw] sm:text-[5.5vw] md:text-[4vw] lg:text-[2.8vw] bg-gradient-to-r from-darkblue to-primary bg-clip-text text-transparent">
            Why Subscribers Use Livmo Health?  
          </h2>

          <p className="mt-2.5 max-w-[950px] mx-auto text-text1/90 leading-[1.7] font-poppins text-[3.5vw] sm:text-[2.2vw] md:text-[1.4vw] lg:text-[1.02vw]">
            Livmo helps organizations support employee healthcare needs through
            coordinated healthcare access, provider support, and medical
            guidance services across India.
          </p>
        </div>

        {/* FREE Badge Highlight - improved mobile */}
<div className="mt-6 lg:mt-8 flex justify-center px-3 sm:px-4">
  <div className="relative overflow-hidden bg-gradient-to-r from-royalBlue to-royalGreen rounded-2xl shadow-xl w-full max-w-4xl">

    {/* Background glows - hidden on mobile */}
    <div className="hidden md:block absolute top-[-20%] right-[-20%] w-[180px] h-[180px] bg-white/10 rounded-full blur-2xl" />
    <div className="hidden md:block absolute bottom-[-20%] left-[-20%] w-[150px] h-[150px] bg-white/5 rounded-full blur-2xl" />

    <div className="relative z-10 p-4 md:p-6 lg:p-8">

      {/* Header */}
      <div className="flex flex-row items-center gap-3 md:gap-5">
        <div className="w-10 h-10 md:w-14 md:h-14 rounded-full bg-white/15 border border-white/20 md:border-2 flex items-center justify-center shrink-0">
          <BadgeCheck className="text-white w-5 h-5 md:w-7 md:h-7" />
        </div>
        <div>
          <h3 className="text-white font-poppins font-bold leading-tight text-lg md:text-2xl lg:text-3xl">
            <span className="text-[#FFD700]">₹599</span>
            <span className="ml-2 text-sm md:text-lg lg:text-xl">First Year</span>
          </h3>
          <p className="text-white/80 text-xs md:text-sm mt-0.5 md:mt-1">
            For Subscribers – coordinated care
          </p>
        </div>
      </div>

      <div className="my-4 md:my-6 h-px bg-white/20" />

      {/* MOBILE VERSION — screens < 768px */}
      <div className="md:hidden">
        <div className="flex flex-col gap-2">
          {[
            "Priority healthcare coordination",
            "1 yoga session / year",
            "1 doctor consultation / year",
            "1 mobile de-addiction session",
            "Free dental & eye camps",
            "ABHA card registration",
            "PM-JAY & govt scheme check",
            "Health updates & notifications",
            "WhatsApp support",
          ].map((feature, idx) => (
            <div key={idx} className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-[#FFD700] shrink-0" />
              <span className="text-white/95 text-xs leading-normal">
                {feature}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* DESKTOP VERSION — screens >= 768px */}
      <div className="hidden md:block">
        <div className="grid md:grid-cols-2 gap-x-6 gap-y-3">
          {[
            "Priority healthcare coordination support",
            "1 complementary yoga therapy session / year",
            "1 complementary doctor consultation / year",
            "1 complementary mobile de-addiction session",
            "Free participation in Livmo Health Dental Camp",
            "Free participation in Livmo Health Eye Camp",
            "More health benefits via Livmo associated providers",
            "Assistance with ABHA card registration",
            "PM-JAY & other govt scheme eligibility verification",
            "Health awareness updates & notifications",
            "WhatsApp support assistance",
          ].map((feature, idx) => (
            <div key={idx} className="flex items-start gap-2">
              <div className="mt-1 w-3 h-3 rounded-full bg-[#FFD700]/30 border border-[#FFD700]/60 flex items-center justify-center shrink-0">
                <div className="w-1.5 h-1.5 rounded-full bg-[#FFD700]" />
              </div>
              <span className="text-white/95 text-sm md:text-base leading-relaxed">
                {feature}
              </span>
            </div>
          ))}
        </div>
      </div>

    </div>
  </div>
</div>

        {/* Benefits Grid - improved mobile */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 lg:gap-7 mt-[6vw] lg:mt-[3vw]">
          {benefits.map((benefit, index) => (
            <div
              key={index}
              className="group bg-white rounded-xl lg:rounded-2xl border border-gray-100 hover:border-royalBlue/20 p-5 lg:p-7 shadow-md hover:shadow-2xl hover:-translate-y-2 transition-all duration-300 relative overflow-hidden"
            >
              
              {/* Top Accent */}
              <div className="absolute top-0 left-0 w-full h-[3px] lg:h-[4px] bg-gradient-to-r from-royalBlue to-royalGreen"></div>

              {/* Icon - responsive */}
              <div className="w-[55px] h-[55px] lg:w-[65px] lg:h-[65px] rounded-xl lg:rounded-2xl bg-gradient-to-br from-royalBlue/10 to-royalGreen/10 flex items-center justify-center group-hover:scale-110 transition-all duration-300">
                {benefit.icon}
              </div>

              {/* Content */}
              <div className="mt-5 lg:mt-6">
                
                <h3 className="font-poppins font-[700] text-darkblue leading-[1.4] text-[4vw] sm:text-[2.8vw] md:text-[1.8vw] lg:text-[1.15vw] group-hover:text-royalBlue transition-colors duration-300">
                  {benefit.title}
                </h3>

                <p className="mt-3 text-text1/80 leading-[1.7] font-poppins text-[3.2vw] sm:text-[1.9vw] md:text-[1.2vw] lg:text-[0.88vw]">
                  {benefit.description}
                </p>
              </div>

              {/* Bottom Arrow - responsive */}
              <div className="mt-5 lg:mt-6 flex items-center gap-2 text-royalBlue font-poppins font-[600] text-[2.8vw] sm:text-[1.6vw] md:text-[1vw] lg:text-[0.85vw] group-hover:gap-3 transition-all duration-300">
                <span>Subscribers Healthcare Support</span>
                <ArrowRight size={14} className="lg:size-[16px]" />
              </div>
            </div>
          ))}
        </div>

        {/* Bottom CTA - improved mobile */}
        <div className="mt-[6vw] lg:mt-[3vw] bg-white border border-gray-100 rounded-2xl lg:rounded-[2vw] shadow-xl p-6 lg:p-8 hover:shadow-2xl transition-all duration-300">
          
          <div className="flex flex-col lg:flex-row items-center justify-between gap-6 lg:gap-8">
            
            <div className="text-center lg:text-left">
              
              <h3 className="font-poppins font-[700] text-darkblue leading-[1.3] text-[5.5vw] sm:text-[4vw] md:text-[2.5vw] lg:text-[1.8vw]">
                Build Better Employee Healthcare Support Systems
              </h3>

              <p className="mt-3 text-text1/90 leading-[1.7] font-poppins text-[3.2vw] sm:text-[1.8vw] md:text-[1.2vw] lg:text-[0.9vw]">
                Livmo helps organizations improve employee wellbeing by making
                healthcare coordination simpler, faster, and more accessible.
              </p>
            </div>

            <button className="bg-gradient-to-r from-royalBlue to-royalGreen text-white font-poppins font-[600] rounded-xl lg:rounded-2xl px-6 py-3 lg:px-8 lg:py-4 shadow-lg hover:shadow-2xl hover:scale-105 transition-all duration-300 whitespace-nowrap text-[3.5vw] sm:text-[2vw] md:text-[1.1vw] lg:text-[0.9vw]">
              Join as Corporate Partner
            </button>
          </div>
        </div>
      </div>
    </motion.section>
  );
};

export default CorporateSupport;